<template>
	<div class="landing-before-after">
		<h1 class="landing-before-after__heading">{{ heading }}</h1>

		<div class="landing-before-after__carousel">
			<div ref="slider" class="keen-slider landing-before-after__track">
				<div v-for="(card, index) in cards" :key="index" class="keen-slider__slide landing-before-after__card">
					<div class="landing-before-after__image-wrap">
						<img class="landing-before-after__image" :src="card.img" alt="" loading="lazy" />
						<span class="landing-before-after__badge landing-before-after__badge_before">{{ beforeLabel }}</span>
						<span class="landing-before-after__badge landing-before-after__badge_after">{{ afterLabel }}</span>
					</div>
					<div class="landing-before-after__info">
						<p class="landing-before-after__name">{{ card.name }}</p>
						<p class="landing-before-after__lost">{{ card.lost }}</p>
						<p class="landing-before-after__period">{{ card.period }}</p>
					</div>
				</div>
			</div>

			<div class="landing-before-after__dots">
				<span
					v-for="(_, index) in cards"
					:key="index"
					class="landing-before-after__dot"
					:class="{ active: index === activeIndex }"
				></span>
			</div>
		</div>

		<div class="landing-before-after__trustpilot">
			<div class="landing-before-after__trustpilot-row">
				<img class="landing-before-after__trustpilot-logo" :src="trustpilotLogo" alt="Trustpilot" />
				<img class="landing-before-after__trustpilot-stars" :src="trustpilotStars" alt="" />
			</div>
			<p class="landing-before-after__trustpilot-reviews">{{ trustpilotReviews }}</p>
		</div>

		<button
			class="landing-before-after__cta btn"
			type="button"
			data-control="button"
			data-button-type="continue"
			@click="onContinue"
		>
			{{ ctaLabel }}
		</button>
	</div>
</template>

<script>
import 'keen-slider/keen-slider.min.css'
import KeenSlider from 'keen-slider'
import trustpilotLogo from '@/assets/trustpilot-logo.svg'
import trustpilotStars from '@/assets/landing-before-after/trustpilot-stars.svg'

const AUTO_ADVANCE_MS = 3500

export default {
	name: 'LandingBeforeAfter',

	props: {
		heading: {
			type: String,
			required: true,
		},

		cards: {
			type: Array,
			required: true,
		},

		beforeLabel: {
			type: String,
			required: true,
		},

		afterLabel: {
			type: String,
			required: true,
		},

		trustpilotReviews: {
			type: String,
			required: true,
		},

		ctaLabel: {
			type: String,
			required: true,
		},
	},

	// Reuses the landing's existing "advance to next step" signal (handled by LandingView's
	// onProgressLoaderCompleted → goNext) so this screen needs no changes to the landing controller.
	emits: ['progressLoaderCompleted'],

	data() {
		return {
			trustpilotLogo,
			trustpilotStars,
			activeIndex: 0,
			slider: null,
		}
	},

	mounted() {
		this.slider = new KeenSlider(
			this.$refs.slider,
			{
				loop: true,
				mode: 'snap',
				renderMode: 'performance',
				slides: { perView: 'auto', spacing: 12, origin: 'auto' },
				slideChanged: (s) => {
					this.activeIndex = s.track.details.rel
				},
			},
			[this.autoplayPlugin],
		)
	},

	beforeUnmount() {
		this.slider?.destroy()
		this.slider = null
	},

	methods: {
		autoplayPlugin(slider) {
			let timeout
			const clear = () => clearTimeout(timeout)
			const next = () => {
				clear()
				timeout = setTimeout(() => slider.next(), AUTO_ADVANCE_MS)
			}
			slider.on('created', next)
			slider.on('dragStarted', clear)
			slider.on('animationEnded', next)
			slider.on('updated', next)
			slider.on('destroyed', clear)
		},

		onContinue() {
			this.$emit('progressLoaderCompleted')
		},
	},
}
</script>

<style lang="scss" scoped>
.landing-before-after {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-20);
	padding-top: var(--size-24);

	&__heading {
		font-size: var(--size-24);
		font-weight: 700;
		line-height: 1.17;
		color: var(--Text-Primary-100);
		text-align: left;
		width: 100%;
		margin: 0;
	}

	&__carousel {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--size-16);
		width: 100%;
	}

	&__track {
		// Bleed the slider to the right viewport edge (container has 20px side padding) so the
		// next slide peeks flush to the edge, while the first slide stays aligned with the heading.
		align-self: flex-start;
		width: calc(100% + 20px);
	}

	&__card {
		min-width: 290px;
		max-width: 290px;
		background: var(--Background-Primary);
		border-radius: var(--size-20);
		overflow: hidden;
	}

	&__image-wrap {
		position: relative;
		padding: var(--size-8) var(--size-8) 0;
	}

	&__image {
		display: block;
		width: 100%;
		height: 13.75rem;
		object-fit: cover;
		border-radius: var(--size-16);
	}

	&__badge {
		position: absolute;
		bottom: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: var(--size-6) var(--size-10) var(--size-4);
		font-size: var(--size-12);
		font-weight: 700;
		line-height: 1;
		text-transform: uppercase;
		color: var(--Text-White-100);

		&_before {
			left: var(--size-8);
			background: #848698;
			border-radius: var(--size-12) var(--size-12) 0 var(--size-12);
		}

		&_after {
			left: 50%;
			background: #38af76;
			border-radius: var(--size-12) var(--size-12) 0 0;
		}
	}

	&__info {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--size-8);
		padding: var(--size-12) var(--size-16) var(--size-16);
		text-align: left;
	}

	&__name {
		font-size: var(--size-16);
		font-weight: 500;
		line-height: 1.25;
		color: var(--Text-Primary-100);
		margin: 0;
	}

	&__lost {
		font-size: var(--size-24);
		font-weight: 700;
		line-height: 1.17;
		color: var(--Text-Primary-100);
		margin: 0;
	}

	&__period {
		font-size: var(--size-16);
		font-weight: 400;
		line-height: 1.375;
		color: var(--Text-Primary-60);
		margin: 0;
	}

	&__dots {
		display: flex;
		gap: var(--size-6);
	}

	&__dot {
		width: var(--size-6);
		height: var(--size-6);
		border-radius: 50%;
		background: rgba(0, 82, 60, 0.15);
		transition:
			width 0.2s ease,
			background 0.2s ease;

		&.active {
			width: var(--size-16);
			border-radius: var(--size-4);
			background: var(--Fill-Green-100);
		}
	}

	&__trustpilot {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--size-4);
	}

	&__trustpilot-row {
		display: flex;
		align-items: center;
		gap: var(--size-8);
	}

	&__trustpilot-logo {
		height: var(--size-24);
	}

	&__trustpilot-stars {
		height: var(--size-20);
	}

	&__trustpilot-reviews {
		font-size: var(--size-12);
		font-weight: 400;
		line-height: 1.33;
		color: var(--Text-Primary-100);
		margin: 0;
	}

	&__cta {
		width: 100%;
		height: var(--size-48);
		border-radius: var(--size-16);
		background: var(--Fill-Accent-100);
		color: var(--Text-On-Color);
		font-size: var(--size-16);
		font-weight: 600;
	}
}
</style>
